import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Flex, Grid, GridItem, VStack } from '@televet/kibble-ui/build/chakra';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { useUpdatePayoutBatchingSettingMutation, PayoutBatchingSettingsFragment } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { BatchingSchedule, EODSchedule } from './EODSchedule';
import format from 'date-fns/format';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { daysOptions } from 'shared/constants/daysOfWeek';
import { Popover } from '@televet/kibble-ui/build/components/Popover';
import cuid from 'cuid';

interface EODSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  settings?: PayoutBatchingSettingsFragment | null;
  pimsName: string;
}

const getDefaultEndTime = (): Date => {
  const endTime = new Date(0);
  endTime.setHours(18);
  endTime.setMinutes(0);
  endTime.setSeconds(0);
  endTime.setMilliseconds(0);
  return endTime;
};

const defaultSchedules = daysOptions.map((day) => ({
  id: cuid(),
  dayOfWeek: Number(day.value),
  endTime: getDefaultEndTime(),
}));

export const EODSettings = ({ isOpen, onClose, settings, pimsName }: EODSettingsProps): JSX.Element => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const toast = useToast();
  const { currentClinicId, clinicUser } = useClinicUser();
  const [areSettingsAutomated, setAreSettingsAutomated] = useState(false);
  const [batchingSchedules, setBatchingSchedules] = useState<BatchingSchedule[]>(defaultSchedules);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (settings && isOpen) {
      setAreSettingsAutomated(!!settings.isAutomated);

      const transformedSchedules: BatchingSchedule[] = settings.schedules.map((schedule) => ({
        id: schedule.id,
        dayOfWeek: schedule.dayOfWeek,
        endTime: utcToZonedTime(schedule.endTime, userTimezone),
      }));

      setBatchingSchedules(transformedSchedules);
    }
  }, [isOpen, settings, userTimezone]);

  const userName: React.ReactNode = useMemo(() => {
    if (!settings?.updatedByUser) {
      return '';
    }

    const { namePrefix, firstName, lastName } = settings.updatedByUser;
    return (
      <>
        by{' '}
        <Text as="span" fontWeight="semibold">
          {namePrefix} {firstName} {lastName}
        </Text>
      </>
    );
  }, [settings]);

  const lastUpdatedAt: React.ReactNode = useMemo(() => {
    if (!settings?.updatedAt) {
      return '';
    }

    const date = new Date(settings.updatedAt);

    return (
      <>
        on{' '}
        <Text as="span" fontWeight="semibold">
          {format(date, 'M/d/yy')}
        </Text>{' '}
        at{' '}
        <Text as="span" fontWeight="semibold">
          {format(date, `h:mm a`)}
        </Text>
      </>
    );
  }, [settings]);

  const handleUpdateSchedule = useCallback(
    (schedule: BatchingSchedule) => {
      setBatchingSchedules((prev) => {
        const updatedSchedules = prev.map((s) => (s.id === schedule.id ? schedule : s));
        return updatedSchedules;
      });

      if (hasError) {
        setHasError(false);
      }
    },
    [hasError],
  );

  const handleToggleSchedule = useCallback((dayOfWeek: number) => {
    setBatchingSchedules((prev) => {
      const schedule = prev.find((s) => s.dayOfWeek === dayOfWeek);

      if (schedule) {
        return prev.filter((s) => s.dayOfWeek !== dayOfWeek);
      }

      return [...prev, { id: cuid(), dayOfWeek, endTime: getDefaultEndTime() }];
    });
  }, []);

  const handleApplyTimeToAllDays = useCallback((endTime: Date) => {
    setBatchingSchedules((prev) => prev.map((s) => ({ ...s, endTime })));
  }, []);

  const handleClearAllSchedules = useCallback(() => {
    setBatchingSchedules([]);
  }, []);

  const handleSelectAllSchedules = useCallback(() => {
    // Check which days are missing from batchingSchedules
    const missingDays = daysOptions.filter((day) => !batchingSchedules.some((s) => s.dayOfWeek === Number(day.value)));

    // Add missing days to batchingSchedules
    setBatchingSchedules((prev) => {
      return [
        ...prev,
        ...missingDays.map((day) => ({ id: cuid(), dayOfWeek: Number(day.value), endTime: getDefaultEndTime() })),
      ];
    });
  }, [batchingSchedules]);

  const [updatePayoutBatchingSettings, { loading: isUpdatingPayoutBatchingSettings }] =
    useUpdatePayoutBatchingSettingMutation();

  const handleSave = useCallback(async () => {
    if (areSettingsAutomated && !batchingSchedules.length) {
      setHasError(true);
      return;
    }

    const formattedSchedules = batchingSchedules.map((schedule) => ({
      dayOfWeek: schedule.dayOfWeek,
      endTime: zonedTimeToUtc(schedule.endTime, userTimezone),
    }));

    try {
      await updatePayoutBatchingSettings({
        variables: {
          data: {
            clinicId: currentClinicId || '',
            isAutomated: areSettingsAutomated,
            schedules: formattedSchedules,
            updatedByUserId: clinicUser?.id,
          },
        },
      });

      toast({
        title: 'Settings saved successfully!',
        status: 'success',
      });

      onClose();
    } catch {
      toast({
        title: 'Error saving settings',
        description: 'Please try again',
        status: 'error',
      });
    }
  }, [
    areSettingsAutomated,
    batchingSchedules,
    clinicUser?.id,
    currentClinicId,
    onClose,
    toast,
    updatePayoutBatchingSettings,
    userTimezone,
  ]);

  const areAllSchedulesActive = useMemo(() => batchingSchedules.length === daysOptions.length, [batchingSchedules]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalCloseButton />
      <ModalHeader>End of Day Settings</ModalHeader>
      <ModalBody>
        <Flex gap={2} alignItems="flex-start" flexDirection="column">
          {!!lastUpdatedAt && (
            <Alert
              status="info"
              description={
                <Text>
                  Last updated {userName} {lastUpdatedAt}
                </Text>
              }
              hideCloseButton
              w="100%"
              mt={2}
              mb={3}
            />
          )}
          <Switch
            label="Automatically End Day"
            labelStyle={{ fontWeight: 'semibold' }}
            isFieldInline
            isChecked={areSettingsAutomated}
            onChange={(e): void => {
              setAreSettingsAutomated(e.target.checked);
              if (hasError) {
                setHasError(false);
              }
            }}
          />
          <Text size="sm">
            End of Day payment processing will occur automatically at the times specified below. Use the same schedule
            as {pimsName} to ensure accurate reporting.
          </Text>
          {areSettingsAutomated && (
            <Flex w="100%" mt={6} flexDirection="column" gap={3}>
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontWeight="semibold">End Days Automatically:</Text>
                {areAllSchedulesActive ? (
                  <Button variant="ghostNeutral" iconName="arrowCircle" size="xs" onClick={handleClearAllSchedules}>
                    Clear All
                  </Button>
                ) : (
                  <Button variant="ghost" size="xs" onClick={handleSelectAllSchedules}>
                    Select All
                  </Button>
                )}
              </Flex>
              <VStack
                className="Schedules"
                bgColor="background.secondaryAlpha"
                p={3}
                borderRadius="md"
                gap={2}
                alignItems="flex-start"
              >
                <Grid className="Schedules__Header" templateColumns="repeat(6, 1fr)" w="100%" gap={4}>
                  <GridItem colSpan={3}>
                    <Text variant="subtle" textTransform="uppercase" size="xs">
                      Day
                    </Text>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <Popover
                      trigger="hover"
                      anchor={
                        <Box>
                          <Text variant="subtle" textTransform="uppercase" size="xs">
                            End Time
                          </Text>
                        </Box>
                      }
                      placement="top"
                      size="lg"
                    >
                      <Text as="p" size="sm" variant="onContrast" fontWeight="semibold">
                        The cutoff for this day&apos;s transactions.{' '}
                        <Text as="span" size="sm" variant="onContrast" fontWeight="normal">
                          Payments after this time will appear in the next day&apos;s report.
                        </Text>
                      </Text>
                    </Popover>
                  </GridItem>
                  <GridItem>
                    <Popover
                      trigger="hover"
                      anchor={
                        <Box>
                          <Text variant="subtle" textTransform="uppercase" size="xs">
                            Payout Day
                          </Text>
                        </Box>
                      }
                      placement="top"
                      size="lg"
                    >
                      <Text as="p" size="sm" variant="onContrast" fontWeight="semibold">
                        Marks this date as the end of a reporting period.{' '}
                        <Text as="span" size="sm" variant="onContrast" fontWeight="normal">
                          Transactions after this date&apos;s end time will be included in the next Payout Day&apos;s
                          report.
                        </Text>
                      </Text>
                    </Popover>
                  </GridItem>
                </Grid>
                {daysOptions.map((day) => {
                  const schedule = batchingSchedules.find((s) => s.dayOfWeek.toString() === day.value);
                  return (
                    <EODSchedule
                      key={day.label}
                      label={day.label}
                      value={Number(day.value)}
                      schedule={schedule}
                      onUpdateSchedule={handleUpdateSchedule}
                      onToggleSchedule={handleToggleSchedule}
                      onApplyTimeToAllDays={handleApplyTimeToAllDays}
                    />
                  );
                })}
              </VStack>
            </Flex>
          )}
          {hasError && (
            <Text as="p" variant="danger" mt={4}>
              Please select at least one payout day or disable automatically end day.
            </Text>
          )}
        </Flex>
      </ModalBody>
      <ModalFooterButtons
        submitText="Save Changes"
        onSubmit={async (): Promise<void> => {
          await handleSave();
        }}
        isLoading={isUpdatingPayoutBatchingSettings}
      />
    </Modal>
  );
};
